/**
 * Optimized useState/useEffect replacement for TanStack Query's `useQuery`.
 * Now includes in-memory caching, request deduplication, and configurable staleTime.
 */

import { useCallback, useEffect, useRef, useState } from 'react';
import {
  registerQuery,
  getQueryCache,
  fetchDeduplicated,
  type QueryKey,
} from './queryClient';

export interface UseQueryOptions<T> {
  queryKey: QueryKey;
  queryFn: () => Promise<T>;
  enabled?: boolean;
  placeholderData?: (previousData: T | undefined) => T | undefined;
  initialData?: T;
  retry?: number | boolean;
  /** Stale time in milliseconds before re-fetching (default: 30000 = 30s). */
  staleTime?: number;
  gcTime?: number;
  refetchOnMount?: boolean | 'always';
  refetchOnWindowFocus?: boolean | 'always';
}

export interface UseQueryResult<T> {
  data: T | undefined;
  error: unknown;
  isLoading: boolean;
  isPending: boolean;
  isFetching: boolean;
  isError: boolean;
  isSuccess: boolean;
  status: 'pending' | 'success' | 'error';
  refetch: (force?: boolean) => void;
}

const resolveRetries = (retry: number | boolean | undefined): number => {
  if (retry === true) return 3;
  if (typeof retry === 'number') return retry;
  return 0;
};

const DEFAULT_STALE_TIME = 30000; // 30 seconds default cache window

export function useQuery<T>(options: UseQueryOptions<T>): UseQueryResult<T> {
  const {
    queryKey,
    enabled = true,
    initialData,
    staleTime = DEFAULT_STALE_TIME,
  } = options;
  const keyString = JSON.stringify(queryKey);

  // Check initial cache
  const cachedInitial = initialData ?? getQueryCache<T>(keyString, staleTime);

  const [data, setData] = useState<T | undefined>(cachedInitial);
  const [error, setError] = useState<unknown>(null);
  const [isFetching, setIsFetching] = useState(false);
  const [status, setStatus] = useState<'pending' | 'success' | 'error'>(
    cachedInitial !== undefined ? 'success' : 'pending',
  );

  const optionsRef = useRef(options);
  optionsRef.current = options;
  const dataRef = useRef<T | undefined>(data);
  dataRef.current = data;
  const mountedRef = useRef(true);
  const firstRunRef = useRef(true);

  const runFetch = useCallback(
    async (force = false) => {
      if (!mountedRef.current) return;
      if (optionsRef.current.enabled === false) return;

      const effectiveStaleTime = optionsRef.current.staleTime ?? DEFAULT_STALE_TIME;

      // Serve from cache if available and not forced
      if (!force) {
        const cached = getQueryCache<T>(keyString, effectiveStaleTime);
        if (cached !== undefined) {
          if (mountedRef.current) {
            setData(cached);
            setError(null);
            setStatus('success');
            setIsFetching(false);
          }
          return;
        }
      }

      setIsFetching(true);
      const maxRetries = resolveRetries(optionsRef.current.retry);
      let attempt = 0;

      for (;;) {
        try {
          const result = await fetchDeduplicated<T>(
            keyString,
            optionsRef.current.queryFn,
          );
          if (!mountedRef.current) return;
          setData(result);
          setError(null);
          setStatus('success');
          break;
        } catch (err) {
          if (attempt < maxRetries) {
            attempt += 1;
            continue;
          }
          if (!mountedRef.current) return;
          setError(err);
          setStatus('error');
          break;
        }
      }
      if (mountedRef.current) setIsFetching(false);
    },
    [keyString],
  );

  // Register with invalidation registry
  const entryRef = useRef({ key: queryKey, refetch: runFetch });
  entryRef.current.key = queryKey;
  entryRef.current.refetch = runFetch;

  useEffect(() => {
    mountedRef.current = true;
    const unsubscribe = registerQuery(entryRef.current);
    return () => {
      mountedRef.current = false;
      unsubscribe();
    };
  }, []);

  // Fetch on mount, key change, or when enabled becomes true
  useEffect(() => {
    if (!enabled) return;

    if (!firstRunRef.current) {
      const placeholder = optionsRef.current.placeholderData;
      if (placeholder) {
        const next = placeholder(dataRef.current);
        setData(next);
        if (next === undefined) setStatus('pending');
      } else {
        const cached = getQueryCache<T>(keyString, optionsRef.current.staleTime ?? DEFAULT_STALE_TIME);
        if (cached !== undefined) {
          setData(cached);
          setStatus('success');
        } else {
          setData(undefined);
          setStatus('pending');
        }
      }
    }
    firstRunRef.current = false;
    void runFetch(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [keyString, enabled]);

  const isPending = status === 'pending';

  return {
    data,
    error,
    isLoading: isFetching && data === undefined,
    isPending,
    isFetching,
    isError: status === 'error',
    isSuccess: status === 'success',
    status,
    refetch: runFetch,
  };
}
